import React, { useState, useEffect } from 'react';
import {
  FloatingDrawer,
  FloatingDrawerHeader,
  FloatingDrawerBody,
  FloatingDrawerFooter,
} from '@/components/ui/floating-drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Copy, Check, Key, Loader2 } from 'lucide-react';

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (newPassword: string) => Promise<void>;
  memberName: string;
  memberEmail: string;
  loading?: boolean;
}

export const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  memberName,
  memberEmail,
  loading = false
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isGenerated, setIsGenerated] = useState(false);
  const [copied, setCopied] = useState(false);

  // Clear form when dialog is closed
  useEffect(() => {
    if (!open) {
      setNewPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      setShowConfirmPassword(false);
      setErrors({});
      setIsGenerated(false);
      setCopied(false);
    }
  }, [open]);

  const validatePassword = (password: string) => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    if (!/(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: { [key: string]: string } = {};
    
    // Validate new password
    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      newErrors.newPassword = passwordErrors.join(', ');
    }
    
    // Validate confirm password
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    await onConfirm(newPassword);
    
    // Clear form after successful submission
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  /**
   * Single close path, shared by Cancel, Esc, the header X and the scrim.
   *
   * Never dismiss while the change is in flight: the password may already have
   * been reset on the server, and tearing the panel down mid-request would hide
   * that from the admin who has to hand the new password over.
   */
  const handleOpenChange = (next: boolean) => {
    if (!next && loading) return;
    if (!next) {
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
    onOpenChange(next);
  };

  const handleClose = () => handleOpenChange(false);

  const generateRandomPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setNewPassword(password);
    setConfirmPassword(password);
    setErrors({});
    setIsGenerated(true);
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    if (newPassword) {
      await navigator.clipboard.writeText(newPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <FloatingDrawer
      open={open}
      onOpenChange={handleOpenChange}
      side="right"
      width={480}
      className="overflow-hidden"
    >
      <FloatingDrawerHeader
        title="Change password"
        subtitle="Set a new password for this team member"
        icon={<Key className="h-4 w-4" />}
      />

      {/* The submit button lives in the footer, OUTSIDE this form — they are
          associated by `form="change-password-form"`, which works across the
          drawer's flex layout exactly as it did across the dialog's. */}
      <FloatingDrawerBody>
        <form id="change-password-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="py-2">
            <p className="text-sm text-gray-700">
              Changing password for <span className="font-semibold">{memberName}</span>
            </p>
            <p className="text-xs text-gray-500">
              {memberEmail}
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="newPassword">New Password</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateRandomPassword}
                  className="text-xs"
                >
                  Generate Random
                </Button>
              </div>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setIsGenerated(false); setCopied(false); }}
                  placeholder="Enter new password"
                  className={errors.newPassword ? "border-red-500" : ""}
                />
                <div className="absolute right-0 top-0 h-full flex items-center">
                  {isGenerated && newPassword && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="px-2 py-2 hover:bg-transparent"
                      onClick={handleCopyPassword}
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              {errors.newPassword && (
                <p className="text-xs text-red-600">{errors.newPassword}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className={errors.confirmPassword ? "border-red-500" : ""}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {errors.confirmPassword && (
                <p className="text-xs text-red-600">{errors.confirmPassword}</p>
              )}
            </div>
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-xs text-blue-700">
              <strong>Password Requirements:</strong> At least 8 characters, including uppercase, lowercase, number, and special character.
            </p>
          </div>
        </form>
      </FloatingDrawerBody>

      <FloatingDrawerFooter>
        <Button
          type="button"
          variant="outline"
          className="rounded-[8px]"
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="change-password-form"
          disabled={loading || !newPassword || !confirmPassword}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Changing...
            </>
          ) : (
            'Change Password'
          )}
        </Button>
      </FloatingDrawerFooter>
    </FloatingDrawer>
  );
};
